/**
 * Client-side seller photo validation (M14).
 */
export const SELLER_PHOTO_LIMITS = Object.freeze({
  maxFiles: 12,
  maxBytes: 8 * 1024 * 1024,
  allowedMimeTypes: ["image/jpeg", "image/png", "image/webp"],
  accept: "image/jpeg,image/png,image/webp",
});

export const SELLER_PHOTO_VALIDATION_MESSAGES_ES = Object.freeze({
  tooMany: "Puedes seleccionar hasta 12 fotos.",
  tooLarge: "Cada foto debe pesar menos de 8 MB.",
  invalidType: "Solo se permiten imágenes JPG, PNG o WebP.",
  empty: "El archivo está vacío.",
});

/**
 * @param {File} file
 */
export function validateSellerPhotoFile(file) {
  if (!file || !file.size) {
    return { ok: false, message: SELLER_PHOTO_VALIDATION_MESSAGES_ES.empty };
  }
  if (file.size > SELLER_PHOTO_LIMITS.maxBytes) {
    return { ok: false, message: SELLER_PHOTO_VALIDATION_MESSAGES_ES.tooLarge };
  }
  if (!SELLER_PHOTO_LIMITS.allowedMimeTypes.includes(file.type)) {
    return { ok: false, message: SELLER_PHOTO_VALIDATION_MESSAGES_ES.invalidType };
  }
  return { ok: true };
}

/**
 * @param {File[]} files
 * @param {number} existingCount
 */
export function validateSellerPhotoSelection(files = [], existingCount = 0) {
  if (existingCount + files.length > SELLER_PHOTO_LIMITS.maxFiles) {
    return { ok: false, message: SELLER_PHOTO_VALIDATION_MESSAGES_ES.tooMany };
  }

  for (const file of files) {
    const result = validateSellerPhotoFile(file);
    if (!result.ok) return result;
  }

  return { ok: true, files };
}

const PREVIEW_MAX_DIMENSION = 480;

/**
 * @param {File} file
 * @param {number} maxDim
 */
async function createResizedPreviewUrl(file, maxDim = PREVIEW_MAX_DIMENSION) {
  if (typeof window === "undefined") {
    return null;
  }

  if (!window.createImageBitmap || typeof document === "undefined") {
    return URL.createObjectURL(file);
  }

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height, 1));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      bitmap.close?.();
      return URL.createObjectURL(file);
    }
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.82);
    });
    if (!blob) {
      return URL.createObjectURL(file);
    }
    return URL.createObjectURL(blob);
  } catch {
    return URL.createObjectURL(file);
  }
}

/**
 * @param {File} file
 */
export async function createSellerPhotoPreviewEntry(file) {
  const previewUrl = (await createResizedPreviewUrl(file)) || URL.createObjectURL(file);
  return {
    id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
    file,
    previewUrl,
    name: file.name,
    size: file.size,
    type: file.type,
  };
}

/**
 * @param {{ previewUrl?: string|null }[]} entries
 */
export function revokeSellerPhotoPreviews(entries = []) {
  entries.forEach((entry) => {
    if (entry?.previewUrl) {
      URL.revokeObjectURL(entry.previewUrl);
    }
  });
}
